import type { CSSProperties, ReactNode } from 'react';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import type { IFloorZone, IFloorZoneRow, IZoneSweep } from './useZoneSweep';
import styles from './FloorZoneGrid.module.css';

// ---------------------------------------------------------------------------
// FloorZoneGrid — one row per floor: a floor button, then a track of zone
// cells, every cell a target of the press-drag sweep (`useZoneSweep`).
//
// Two surfaces ask their question on this layout with different looks: the
// area map (AreaGridPicker — which zones the work covers) and the progress
// board (TaskProgressSection — which of them are done). The grid owns the
// structure and the gesture wiring; each surface passes its look (`classes`,
// a cell's `className`), what its floor button does, and what each cell shows,
// says, and sweeps.
// ---------------------------------------------------------------------------

/** The floor button of one row. */
export interface IFloorButton {
  /** Absent: the floor is a plain label — nothing to do on it for this user. */
  onClick?: () => void;
  /** A count beside the name. Hidden from assistive tech — the label carries it. */
  count?: ReactNode;
  ariaLabel?: string;
  ariaPressed?: boolean;
  /** What the button does, on hover (the `hint` tooltip). */
  hint?: string;
}

/** One zone cell. */
export interface IZoneCell<TMeta> {
  className?: string;
  ariaLabel: string;
  ariaPressed?: boolean;
  /** What a press on this cell sweeps across the range — `null`: it starts no sweep. */
  pressMeta: TMeta | null;
  /** A plain tap — click, Enter or Space; never the click that trails a sweep.
   *  Absent: the cell only shows (a user with nothing to report on it). */
  onTap?: () => void;
  content: ReactNode;
}

/** A surface's look, one class per part, each added to the grid's own. */
export interface IFloorZoneGridClasses {
  root?: string;
  row?: string;
  floorBtn?: string;
  floorName?: string;
  floorCount?: string;
  track?: string;
}

interface IFloorZoneGridProps<TMeta, Z extends IFloorZone> {
  rows: readonly IFloorZoneRow<Z>[];
  sweep: IZoneSweep<TMeta>;
  classes: IFloorZoneGridClasses;
  /** Names the grid as a group — a picker names its question; a board need not. */
  ariaLabel?: string;
  /** Show, take no answer — a TRANSIENT state (a frozen scope). Disabled buttons get no pointer events, so no sweep can start either. */
  disabled?: boolean;
  rowStyle?: (rowIndex: number) => CSSProperties;
  floor: (row: IFloorZoneRow<Z>) => IFloorButton;
  cell: (zone: Z, row: IFloorZoneRow<Z>) => IZoneCell<TMeta>;
}

/** The grid's own class for a part plus the surface's, whichever exist. */
const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

export function FloorZoneGrid<TMeta, Z extends IFloorZone>({
  rows,
  sweep,
  classes,
  ariaLabel,
  disabled = false,
  rowStyle,
  floor,
  cell,
}: IFloorZoneGridProps<TMeta, Z>) {
  return (
    <div
      className={cx(classes.root, sweep.sweeping && styles.sweeping)}
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
    >
      {rows.map((row, rowIndex) => {
        const button = floor(row);
        const floorLabel = (
          <>
            <span className={cx(styles.floorName, classes.floorName)}>{row.name}</span>
            {button.count != null && (
              <span className={cx(styles.floorCount, classes.floorCount)} aria-hidden>{button.count}</span>
            )}
          </>
        );
        // Where this row starts in the flattened order the sweep walks.
        const first = rows.slice(0, rowIndex).reduce((count, above) => count + above.zones.length, 0);
        return (
          <div key={row.id} className={cx(styles.row, classes.row)} style={rowStyle?.(rowIndex)}>
            {button.onClick ? (
              <Tooltip content={button.hint}>
                <button
                  type="button"
                  className={cx(styles.floorBtn, classes.floorBtn)}
                  disabled={disabled}
                  aria-label={button.ariaLabel}
                  aria-pressed={button.ariaPressed}
                  onClick={button.onClick}
                >
                  {floorLabel}
                </button>
              </Tooltip>
            ) : (
              // No handler: a plain element, no button (role-permissions ruling 8).
              <div className={cx(styles.floorBtn, classes.floorBtn)}>{floorLabel}</div>
            )}
            <div className={cx(styles.track, classes.track)}>
              {row.zones.map((zone, zoneIndex) => {
                const index = first + zoneIndex;
                const spec = cell(zone, row);
                const { pressMeta, onTap } = spec;
                if (!onTap) {
                  return (
                    <div key={zone.id} role="img" className={cx(styles.cell, spec.className)} aria-label={spec.ariaLabel}>
                      {spec.content}
                    </div>
                  );
                }
                return (
                  <button
                    key={zone.id}
                    type="button"
                    className={cx(styles.cell, spec.className)}
                    disabled={disabled}
                    aria-label={spec.ariaLabel}
                    aria-pressed={spec.ariaPressed}
                    // The sweep rides pointer events, so a press released over
                    // another cell still counts and a drag takes the whole
                    // range; the click keeps the plain tap (and Enter / Space).
                    onPointerDown={(e) => { if (pressMeta !== null) sweep.start(e, index, pressMeta); }}
                    onPointerEnter={() => sweep.extend(index)}
                    onClick={() => { if (!sweep.shouldSuppressClick()) onTap(); }}
                  >
                    {spec.content}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
